/* Core & libraries */
import React, { useEffect, useState, useRef, Suspense } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { storageFactory } from 'factory/storageFactory';

/* Helpers, constants, custom methods, internal libraries */
import { useLocaleContext } from '../../contexts/localeContext';
import { API_HOST, API_PROTOCOL } from '../../constants/appConstants';
import { useUserContext, useUserUpdateContext } from "../../contexts/userContext";
import { getFullLanguageText, isAdmin } from "../../helpers/general.js";
import { ADMIN_COMPANIES_ROUTE } from "../../constants/routes.js";
import { useHasPermission } from "../../hooks/useHasPermission";
import { AIRLINES } from "../../constants/appConstants";
import { lazyWithPreload } from '../../helpers/lazyLoadingHelpers';
import { useFormatLocale } from '../../hooks/useFormatLocale';

/* Components */
import UCButton from "../../components/buttons";
import { ReactComponent as AALogo } from "../../resources/images/aa-logo.svg"
import { ReactComponent as ASLogo } from "../../resources/images/as-logo.svg"
import { ReactComponent as CHLogo } from "../../resources/images/choice-business-direct-logo.svg"
import { ReactComponent as SignOutIcon } from '../../resources/images/logout.svg';
import SpinnerBasic from '../../components/spinners/SpinnerBasic';
const CountryLanguageModalLazy = lazyWithPreload(() => import('../../components/modals/countryLanguageModal'));
import FlagIconComponent from '../flagIconComponent';

/* Styles */
import styles from "./styles.js";

const localStore = storageFactory(() => localStorage);

const CompanyDeletedContent = () => {

    const theme = useTheme();
    const classes = styles({ theme });
    const locale = useLocaleContext();
    const userContext = useUserContext();
    const setUserContext = useUserUpdateContext();
    const { checkPermission } = useHasPermission();
    const navigate = useNavigate();
  const { locales } = useFormatLocale();

    const providerCode = userContext?.userInfo?.providerInfo?.providerCode;

    const PROVIDER_CONTENT = {
        [AIRLINES.AA]: {
            Logo: AALogo,
            homeUrl: "https://www.aa.com",
            websiteMsgId: "companyDeleted.aaWebsiteLink",
        },
        [AIRLINES.AS]: {
            Logo: ASLogo,
            homeUrl: "https://www.alaskaair.com",
            websiteMsgId: "companyDeleted.asWebsiteLink",
        },
        [AIRLINES.CHOICE]: {
            Logo: CHLogo,
            homeUrl: "https://www.choicehotels.com",
            websiteMsgId: "companyDeleted.chWebsiteLink",
        },
    };

    const providerCfg = PROVIDER_CONTENT[providerCode] ?? {
        Logo: null,
        homeUrl: "#",
        websiteMsgId: "companyDeleted.providerWebsiteLink", // add this generic i18n key
    };

    const handleManageCompanies = async () => {
        await setUserContext({
            ...userContext,
            userInfo: {
                ...userContext.userInfo,
                companyInfo: null,
            },
            manageCompany: false,
        });
        navigate(ADMIN_COMPANIES_ROUTE);
    };

    const logout = () => {
        const LOGOUT_URL = "oidc/logout.html"
        window.location.href = `${API_PROTOCOL}://${API_HOST}/${LOGOUT_URL}`;
        localStore.removeItem("showAuthorisedCityUsersModal")
        localStore.removeItem("maintenanceMode")
    };

    const [showLanguageModal, setShowLanguageModal] = useState(false);

    return (
      <div className={classes.companyDeletedContent}>
        <Col xs={12} className="gx-0">
          <div className={classes.headerContainer}>
            <div>
              <div
                tabIndex="0"
                onClick={() => setShowLanguageModal(true)}
                className={classes.overlayLink}
                data-testid={'langselector'}
              >
                <FlagIconComponent locale={locale} flagClassName={classes.flagStyle}/>
                <div className={classes.locale}>{getFullLanguageText(locale)}</div>
              </div>
            </div>
            <div>
              <Link onClick={logout} className={classes.overlayLinkSignOut}>
                <SignOutIcon className={classes.popoverImage} />
                <FormattedMessage id="navigation.signOut" />
              </Link>
            </div>
            <Suspense fallback={showLanguageModal ? <SpinnerBasic /> : null}>
              {showLanguageModal && (
                <CountryLanguageModalLazy showModal={showLanguageModal} setShowModal={setShowLanguageModal} locales={locales}/>
              )}
            </Suspense>
          </div>
        </Col>
        <Container className={classes.companyDeletedContainer}>
          <Row>
            {providerCfg.Logo ? <providerCfg.Logo /> : null}
            <h3 className={classes.title}>
              <FormattedMessage id="companyDeleted.title" />
            </h3>
            {providerCode === AIRLINES.AA ? (
              <>
                <p className={classes.description}>
                  <FormattedMessage id="companyDeleted.description" />
                </p>
                <p className={classes.description2}>
                  <FormattedMessage id="companyDeleted.description2" />
                </p>
              </>
            ) : (
              <p className={classes.description2}>
                <FormattedMessage id="companyDeleted.genericDescription" />
              </p>
            )}
            <div className={classes.returnLink}>
              <span className={classes.returnText}>
                <FormattedMessage id="companyDeleted.returnTo" />
              </span>
              <UCButton
                onClick={() =>
                  checkPermission('F_VIEW_PROVIDER_COMPANIES')
                    ? handleManageCompanies()
                    : (window.location.href = providerCfg.homeUrl)
                }
                type="defaultLink"
                className={classes.aaWebsiteLink}
                text={
                  checkPermission('F_VIEW_PROVIDER_COMPANIES') ? (
                    <FormattedMessage id="companyDeleted.companyListLink" />
                  ) : (
                    <FormattedMessage id={providerCfg.websiteMsgId} />
                  )
                }
              />
            </div>
          </Row>
        </Container>
      </div>
    );
};

export default CompanyDeletedContent;